import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import Icons from '../../assets/index';
import Loader from '../Loader/Loader';

interface IGenericTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  isLoading: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    onPageChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  totalItems: number;
  maxHeight?: string;
  isPagination?: boolean;
  pageTitle?: string;
  emptyMessage?: string;
}

function Table<T>({
  data,
  columns,
  isLoading,
  pagination,
  totalItems,
  isPagination = true,
  maxHeight = '400px',
  pageTitle = 'Districts per page',
  emptyMessage = 'No data found',
}: IGenericTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: pagination.pageCount,
    state: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === 'function'
          ? updater({
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            })
          : updater;

      if (newState.pageIndex !== pagination.pageIndex) {
        pagination.onPageChange(newState.pageIndex);
      }

      if (newState.pageSize !== pagination.pageSize) {
        pagination.onPageSizeChange(newState.pageSize);
      }
    },
  });

  return (
    <div className="w-full flex flex-col h-full">
      {/* Scrollable Table Section */}
      <div className="flex-grow overflow-auto border-t  border-gray-200" style={{ maxHeight }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-blue-950 top-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`px-3 py-3 text-left text-sm font-semibold text-gray-550 border-l border-gray-200 ${
                      index === headerGroup.headers.length - 1 ? 'border-r border-gray-200' : ''
                    }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div
                    className="text-center flex items-center justify-center h-full py-10"
                    data-testid="loader"
                  >
                    <Loader color="primary" size="xl" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody className="bg-white divide-gray-200">
              {table.getRowModel()?.rows?.length === 0 ? (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="text-center py-6 text-gray-2400 text-sm"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, rowIndex) => {
                  const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                  return (
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50 ${isLastRow ? 'border-b border-gray-200' : ''}`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const isLastCell = cellIndex === row.getVisibleCells().length - 1;
                        return (
                          <td
                            key={cell.id}
                            className={`px-2 py-2 whitespace-nowrap border-l border-gray-200 text-gray-550 text-sm font-medium ${
                              isLastCell ? 'border-r border-gray-200' : ''
                            }`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination Section - Now below the scrollable area */}
      {isPagination && data.length > 0 && (
        <div className="flex items-center justify-end px-6 py-2 bg-white flex-wrap gap-4 ">
          {/* Left - Page size selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-ds-gray-500">{pageTitle}</span>
            <div className="relative">
              <select
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                className="appearance-none border border-gray-200 rounded-md text-sm px-3 py-1.5 pr-8 focus:outline-none text-ds-gray-500"
              >
                {[10, 20, 30, 40].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              {/* Dropdown icon */}
              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-gray-500">
                <Icons.arrowDownIcon />
              </div>
            </div>
          </div>

          {/* Right - Pagination navigation */}
          <div className="flex items-center space-x-4">
            <span className="text-sm text-ds-gray-500">
              {pagination.pageIndex * pagination.pageSize + 1} to{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalItems)} of{' '}
              {totalItems}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => pagination.onPageChange(pagination.pageIndex - 1)}
                disabled={pagination.pageIndex === 0}
                className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icons.previousIcon />
              </button>
              <button
                onClick={() => pagination.onPageChange(pagination.pageIndex + 1)}
                disabled={pagination.pageIndex >= pagination.pageCount - 1}
                className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icons.nextIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Table;
